import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * What Lead Agent's indicator in the chat says (AGI-500), per the "Agent
 * states" table of the DS motion contract (guidelines/motion.md):
 * - `working`: Lead Agent is preparing a reply;
 * - `slow`: the wait passed LONG_WAIT_MS; the text says so and the pulse goes on;
 * - `noReply`: the wait passed NO_REPLY_MS; the pulse stops and a static state
 *   offers the conversation to the person.
 */
export type tLeadAgentIndicatorStage = 'working' | 'slow' | 'noReply';

// Product thresholds, counted from chatbot-service's processing event. They are
// not motion values.
export const LONG_WAIT_MS = 15_000;
export const NO_REPLY_MS = 45_000;

type tUseLeadAgentIndicatorProps = {
  chatId: string | undefined;
  // Whether Lead Agent attends this chat: the chat context's autoRespondEnabled.
  agentEnabled: boolean;
};

export type tLeadAgentIndicator = {
  stage: tLeadAgentIndicatorStage | null;
  // The last wait ended with Lead Agent's reply (the contract's Done state).
  // It holds until a new wait starts, stop() clears it or the chat changes.
  replied: boolean;
  // chatbot-service is processing a message for this chat: the wait (re)starts.
  start: () => void;
  // A Lead Agent message arrived: it ends the wait, if there is one, as Done.
  done: () => void;
  // The wait ended for any other reason: a handoff, someone took the chat.
  stop: () => void;
};

type tIndicatorState = {
  stage: tLeadAgentIndicatorStage | null;
  replied: boolean;
};

const IDLE: tIndicatorState = { stage: null, replied: false };

export default function useLeadAgentIndicator(
  props: tUseLeadAgentIndicatorProps,
): tLeadAgentIndicator {
  const [state, setState] = useState<tIndicatorState>(IDLE);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const stop = useCallback(() => {
    clearTimers();
    setState((prev) => (prev.stage || prev.replied ? IDLE : prev));
  }, [clearTimers]);

  // Only a reply that ends a wait is Done; a second message, or one with no
  // wait before it, changes nothing, so Done is announced once.
  const done = useCallback(() => {
    clearTimers();
    setState((prev) => (prev.stage ? { stage: null, replied: true } : prev));
  }, [clearTimers]);

  const start = useCallback(() => {
    clearTimers();
    setState({ stage: 'working', replied: false });
    timersRef.current = [
      setTimeout(
        () => setState({ stage: 'slow', replied: false }),
        LONG_WAIT_MS,
      ),
      setTimeout(
        () => setState({ stage: 'noReply', replied: false }),
        NO_REPLY_MS,
      ),
    ];
  }, [clearTimers]);

  // Another chat starts with no indicator, and no timer outlives the thread.
  useEffect(() => {
    stop();
    return clearTimers;
  }, [props.chatId]);

  // Someone took the conversation: whoever switched Lead Agent off for this
  // chat (the indicator's own button, the chat's switch, another tab, a stage
  // change) ends the wait. Only the switch from on to off counts, because the
  // flag also starts off while a chat's state loads.
  const wasEnabledRef = useRef(props.agentEnabled);
  useEffect(() => {
    if (wasEnabledRef.current && !props.agentEnabled) stop();
    wasEnabledRef.current = props.agentEnabled;
  }, [props.agentEnabled]);

  return { stage: state.stage, replied: state.replied, start, done, stop };
}
